import * as Sentry from '@sentry/browser';


const defaultState = () => ({
  contentFollows: [],
  lastFetchedAt: null,
  stats: { flashcard: [] },
  currentCredits: null,
  creditsUpdatedAt: null,
  creditsResetAt: null,
  user: null,
});

const defaultGetters = {
  answeredCount: (state) => state.stats['flashcard'].map(day => day['count']).reduce((a, b) => a + b, 0),
  contentFollows: (state) => state.contentFollows,
  get: (state) => state.user,
  // The user's flag, already resolved against the app's setting by the server; flag names arrive camelCased, e.g. featureQuestionSets
  featureEnabled: (state) => (flagName) => state.user?.featureFlags?.[flagName]?.state === true,
  // True when the user should be shown the opt-in for this flag
  featureConsentPending: (state) => (flagName) => state.user?.featureFlags?.[flagName]?.presentConsent === true,
  marketingSegment: (state) => state.user?.marketingSegment,
  lastFetchedAt: (state) => state.lastFetchedAt,
  rateLimitCredits: state => state.currentCredits,
  rateLimitingEnabled: (state) => state.user?.rateLimitingEnabled,
  rateLimitCreditsUpdatedAt: (state) => state.creditsUpdatedAt,
  rateLimitCreditsResetAt: (state) => state.creditsResetAt,
  stats: (state) => state.stats,
  userQuizzes: (state) => state.userQuizzes,
};

const mutations = {
  incrementStats(state) {
    // Local date, matching the server's per-day keys in the user's time zone
    const now = new Date();
    const pad = n => String(n).padStart(2, '0');
    let date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    let index = state.stats['flashcard'].findIndex(d => d['date'] === date);
    if (index > -1) {
      let day = state.stats['flashcard'][index];
      day['count'] = day['count'] + 1;
      state.stats['flashcard'][index] = day;
    } else {
      state.stats['flashcard'].push({ count: 1, date: date });
    }
  },
  setFeatureFlags(state, featureFlags) {
    if (state.user) {
      state.user = { ...state.user, featureFlags };
    }
  },
  set(state, user) {
    state.lastFetchedAt = Date.now();
    if (user) {
      // Pulling these off the user so that we don't end up with stale data on the user object and have a conflict anywhere
      const {
        contentFollows,
        currentCredits,
        creditsUpdatedAt,
        creditsResetAt,
        stats,
        userQuizzes,
        ...remainingUserAttrs
      } = user;

      if (creditsResetAt) {
        state.creditsResetAt = new Date(creditsResetAt);
      }
      if (creditsUpdatedAt) {
        state.creditsUpdatedAt = new Date(creditsUpdatedAt);
      }
      if (stats) {
        state.stats = stats;
      }

      state.contentFollows = contentFollows;
      state.currentCredits = currentCredits;
      state.userQuizzes = userQuizzes;
      state.user = remainingUserAttrs;
    } else {
      state.user = user;
      state.contentFollows = [];
      state.stats = { flashcard: [] };
    }

    if (user && user.id) {
      Sentry.setUser({ id: user.id });
    } else {
      Sentry.setUser(null);
    }
  },
  setCreditsResetAt(state, date) {
    state.creditsResetAt= date;
  },
  setCreditsUpdatedAt(state, date) {
    state.creditsUpdatedAt= date;
  },
  setCurrentCredits(state, credits) {
    state.currentCredits = credits;
  },
  setContentFollows(state, follows) {
    state.contentFollows = follows;
  },
};

const actions = {
  awardRateLimitCredits({ dispatch, commit, getters, rootGetters }, credits = rootGetters['app/rateLimitCycleCredits']) {
    const params = {
      credits,
      time: new Date().toTimeString(),
    };
    return rootGetters['session/ajax'].patch('/user/rate_limit', params).then(({ data }) => {
      const { currentCredits, creditsUpdatedAt, creditsResetAt } = data ;
      commit('setCreditsResetAt', new Date(creditsResetAt));
      commit('setCreditsUpdatedAt', new Date(creditsUpdatedAt));
      commit('setCurrentCredits', currentCredits);
      return data;
    });
  },
  changeSubscription({ dispatch, commit, getters, rootGetters }, params) {
    return rootGetters['session/ajax'].post('/subscriptions/change_plan', params).then(({ data }) => {
      if (data.user) {
        dispatch('setUser', data.user);
      }
    });
  },
  decrementRateLimitCredits({ dispatch, commit, getters, rootGetters }) {
    const currentUserCredits = getters.rateLimitCredits;
    const newCredits = currentUserCredits <= 0 ? 0 : currentUserCredits - 1;
    const params = {
      credits: newCredits,
      time: new Date().toTimeString(),
    };
    return rootGetters['session/ajax'].patch('/user/rate_limit', params).then(({ data }) => {
      const { currentCredits, creditsUpdatedAt  } = data;
      commit('setCreditsUpdatedAt', new Date(creditsUpdatedAt));
      commit('setCurrentCredits', currentCredits);
      return { ...data, currentUserCredits, newCredits };
    });
  },
  spendCredit({ dispatch, commit, getters, rootGetters }) {
    const currentUserCredits = getters.rateLimitCredits;
    const newCredits = currentUserCredits <= 0 ? 0 : currentUserCredits - 1;
    return rootGetters['session/ajax'].post('/user/spend_credits').then(({ data }) => {
      const { remainingCredits, creditsUpdatedAt  } = data;
      commit('setCreditsUpdatedAt', new Date(creditsUpdatedAt));
      commit('setCurrentCredits', remainingCredits);
      return { ...data, currentUserCredits, newCredits };
    }).catch((error) => {
      console.error('Error spending credit', error);
    });
  },
  fetchContentPartnerAssets({ dispatch, commit, getters, rootGetters }, contentPartnerId) {
    return rootGetters['session/ajax'].get('/content_partners/assets', { id: contentPartnerId });
  },
  followContentPartner({ dispatch, commit, getters, rootGetters }, contentPartnerId) {
    return rootGetters['session/ajax'].post('/content_follows', { appContentPartnerId: contentPartnerId }).then(response => {
      const follow = { id: response.data.id, contentPartnerId: response.data.appContentPartnerId };
      commit('setContentFollows', [...getters['contentFollows'], follow]);
    });
  },
  // Sends the user's answer to an opt-in; flagName is camelCased, like the getters
  respondToFeatureFlag({ commit, rootGetters }, { flagName, state }) {
    const snakeName = flagName.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
    return rootGetters['session/ajax'].patch(`/feature_flags/${snakeName}`, { state }).then(response => {
      commit('setFeatureFlags', response.data.featureFlags);
      return response.data.featureFlags;
    });
  },
  setUser({ dispatch, commit, getters, rootGetters }, user) {
    commit('set', user);
    commit('purchaseReceipts/set', user.purchaseReceipts, { root: true });
  },
  unfollowContentPartner({ dispatch, commit, getters, rootGetters }, contentFollowId) {
    return rootGetters['session/ajax'].delete(`/content_follows/${contentFollowId}`).then(response => {
      commit('setContentFollows', getters['contentFollows'].filter(cf => cf.id !== contentFollowId));
    });
  },
  update({ dispatch, commit, getters, rootGetters}, params) {
    return rootGetters['session/ajax'].put('/user', params).then(response => {
      dispatch('setUser', response.data?.user);
      this._vm?.$analytics?.config(rootGetters);

      return response;
    });
  },
  updateAvatar({ dispatch, commit, getters, rootGetters }, formData) {
    return rootGetters['session/ajax']
      .put('/user', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(response => {
        dispatch('setUser', response.data?.user);
        return response;
      });
  },
  updateDatum({ dispatch, commit, getters, rootGetters }) {
    const disabledDashboards = rootGetters['ui/disabledDashboards'];
    const params = {
      data: {
        disabledDashboards: disabledDashboards,
      },
    };
    return rootGetters['session/ajax'].post('user_datum', params);
  },
};

export default {
  namespaced: true,
  state: defaultState,
  getters: defaultGetters,
  mutations,
  actions,
};
